/**
 * Fuel. Every shift starts with a full tank. The tank is measured in units (1 unit = 1% of a tank); a unit buys
 * about 150 m of driving at the least (a full tank is about 15 km), more when you use the gas pedal or the siren. Petrol stations sell
 * units; the server prices them itself. Shared so the server can check the numbers a shift reports.
 */
export const FUEL = {
  tankUnits: 100,
  pesewasPerUnit: 300,
  /** Metres one unit lasts at the very best (cruising). Gas and siren use it up faster. */
  metresPerUnit: 400,
  gasFactor: 1.35,
  sirenFactor: 1.5,
  /** Burned while the engine runs but you're standing still, per second. */
  idleUnitsPerSecond: 0.015,
  /** How fast the pump fills the tank, units per second. */
  fillUnitsPerSecond: 9,
  /** The fuel warning shows at this many units or fewer; it also beeps at each of `warnAt` (20%, 10%, 5%). */
  lowAt: 20,
  /** A new shift never starts with less than this: the tank carries over, but the yard always tops it up a little. */
  minStartUnits: 25,
  warnAt: [20, 10, 5],
} as const;

/** Slack for rounding between the game and the server, in units. */
const SLACK_UNITS = 3;

/** The most fuel a shift could have burned (so the most it could honestly have bought). */
export function maxFuelBought(metres: number, seconds: number, startUnits: number = FUEL.tankUnits): number {
  const perMetre = (FUEL.gasFactor * FUEL.sirenFactor) / FUEL.metresPerUnit;
  // Starting part-full, you can honestly buy what you burned plus what was missing from the tank.
  return Math.ceil(metres * perMetre + seconds * FUEL.idleUnitsPerSecond + SLACK_UNITS + (FUEL.tankUnits - startUnits));
}

/** The least fuel a shift must have bought: anything beyond one tank of range has to be paid for. */
export function minFuelBought(metres: number, startUnits: number = FUEL.tankUnits): number {
  return Math.max(0, Math.floor(metres / FUEL.metresPerUnit - startUnits - SLACK_UNITS));
}
